import type { ActivityAction, Category, NotificationPrefs, Role, Settings } from "./types";

export const APP_NAME = "LOVE CPMD";
export const DEFAULT_MEMBER_PASSWORD = "cpmd2026";
export const DEFAULT_GUEST_PASSWORD = "guest2026";
export const TOPIC_MAX_LENGTH = 100;

/**
 * The nine active categories (decision #11: numbers must match this list).
 * Colors follow a validated categorical order (dataviz validator: CVD ΔE ≥ 9, normal-vision ΔE ≥ 22 on adjacent slots);
 * "Others" is the neutral fold-in color. Light slots sit below 3:1 contrast, so charts always carry text labels.
 */
export const DEFAULT_CATEGORIES: Category[] = [
  { id: "cat-industry", name: "Industry / Business Update", shortName: "Industry", color: "#B8395F", active: true, order: 1 },
  { id: "cat-tech", name: "Technology & Digital", shortName: "Technology", color: "#2A78D6", active: true, order: 2 },
  { id: "cat-selfdev", name: "Self Development", shortName: "Self Dev", color: "#EB6834", active: true, order: 3 },
  { id: "cat-management", name: "Management", shortName: "Management", color: "#1BAF7A", active: true, order: 4 },
  { id: "cat-productivity", name: "Productivity", shortName: "Productivity", color: "#EDA100", active: true, order: 5 },
  { id: "cat-workexp", name: "Work Experience", shortName: "Work Exp", color: "#4A3AA7", active: true, order: 6 },
  { id: "cat-knowledge", name: "Knowledge Sharing", shortName: "Knowledge", color: "#008300", active: true, order: 7 },
  { id: "cat-interesting", name: "Interesting Information", shortName: "Interesting", color: "#E87BA4", active: true, order: 8 },
  { id: "cat-others", name: "Others", shortName: "Others", color: "#8B8589", active: true, order: 9 },
];

export const DEFAULT_SETTINGS: Settings = {
  weekday: 2,
  startTime: "07:30",
  endTime: "09:00",
  location: "CPMD's Division Room",
  reminders: { h7: true, h1: true, dday: true },
};

export const DEFAULT_PREFS: NotificationPrefs = {
  h7: true,
  h1: true,
  dday: true,
  changes: true,
  summaries: false,
};

export const ROLE_LABEL: Record<Role, string> = {
  member: "Member",
  system_manager: "System Manager",
  guest: "Guest",
};

export interface ActionMeta {
  label: string;
  group: "schedule" | "session" | "summary" | "rotation" | "system";
}

export const ACTIVITY_META: Record<ActivityAction, ActionMeta> = {
  CREATE_SCHEDULE: { label: "New schedule", group: "schedule" },
  FILL_FROM_ROTATION: { label: "Filled from rotation", group: "schedule" },
  UPDATE_TOPIC: { label: "Topic updated", group: "schedule" },
  RESCHEDULE: { label: "Rescheduled", group: "schedule" },
  SWAP: { label: "Swap", group: "schedule" },
  CANCEL: { label: "Cancelled", group: "schedule" },
  MARK_DONE: { label: "Session completed", group: "session" },
  REPLACEMENT: { label: "Replacement", group: "session" },
  SPONTANEOUS: { label: "Extra sharing", group: "session" },
  CREATE_SUMMARY: { label: "Summary published", group: "summary" },
  UPDATE_SUMMARY: { label: "Summary edited", group: "summary" },
  GENERATE_ROTATION: { label: "Rotation generated", group: "rotation" },
  REORDER_ROTATION: { label: "Rotation reordered", group: "rotation" },
  LOCK_ROTATION: { label: "Rotation locked", group: "rotation" },
  UNLOCK_ROTATION: { label: "Rotation unlocked", group: "rotation" },
  MEMBER_ADD: { label: "Member added", group: "system" },
  MEMBER_UPDATE: { label: "Member updated", group: "system" },
  PASSWORD_RESET: { label: "Password reset", group: "system" },
  CATEGORY_ADD: { label: "Category added", group: "system" },
  CATEGORY_UPDATE: { label: "Category updated", group: "system" },
  SETTINGS_UPDATE: { label: "Settings updated", group: "system" },
};

export const ACTIVITY_GROUPS: { id: ActionMeta["group"]; label: string }[] = [
  { id: "schedule", label: "Schedule changes" },
  { id: "session", label: "Session day" },
  { id: "summary", label: "Summaries" },
  { id: "rotation", label: "Rotation" },
  { id: "system", label: "System" },
];
